import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ApiService } from "../services/api";
import { settlePendingVideoCredit } from "../utils/videoCreditSettlement";
import "./video_result.scss";

const VideoResult = () => {
  const params = useParams();
  const provider = params.provider || "grok";
  const jobId = params.jobId;
  const navigate = useNavigate();
  const modeMap = {
    a2eWan: "no-limit",
    wan: "nsfw",
    luma: "general",
    grok: "cinematic",
  };
  const createAgainPath = `/create-video/${modeMap[provider] || provider}`;
  const [status, setStatus] = useState("pending");
  const [videoUrl, setVideoUrl] = useState(null);
  const creditSettlementStarted = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const poll = async () => {
      try {
        // API call to your backend getVideoStatus
        const data = await ApiService.getProviderVideoStatus(provider, jobId);
        if (!isMounted) return;

        // Providers can return a few equivalent completed statuses.
        const currentStatus = data.status || data.state;
        setStatus(currentStatus);

        if (
          currentStatus === "completed" ||
          currentStatus === "success" ||
          currentStatus === "done"
        ) {
          // data.videoUrl is the path to your VPS uploads folder

          const videoSrc = data.videoUrl?.startsWith("http")
            ? data.videoUrl
            : `https://api.zulluai.com${data.videoUrl}`;

          setVideoUrl(videoSrc);
          if (!creditSettlementStarted.current) {
            creditSettlementStarted.current = true;
            settlePendingVideoCredit(jobId);
          }

        } else if (currentStatus === "failed") {
          setStatus("failed");
        } else {
          // Still processing, poll again in 4 seconds
          setTimeout(poll, 4000);
        }
      } catch (e) {
        console.error("Polling error:", e);
        setTimeout(poll, 8000); // Wait longer on error
      }
    };

    poll();
    return () => {
      isMounted = false;
    };
  }, [jobId, provider]);

  const handleDownload = async () => {
    if (!videoUrl) return;
    try {
      // Since it's on your VPS, we fetch and blob to force download
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `zullu_ai_${jobId}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback: open in new tab
      window.open(videoUrl, "_blank");
    }
  };

  return (
    <div className="zullu-result-workspace">
      <AnimatePresence mode="wait">
        {videoUrl ? (
          <motion.div
            key="completed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="video-display-card"
          >
            <div className="video-wrapper">
              <video
                src={videoUrl}
                controls
                autoPlay
                loop
                playsInline
                className="main-video"
              />
            </div>

            <div className="result-actions">
              <div className="info">
                <h3>Generation Complete</h3>
                <p>Resolution: High • Format: MP4</p>
              </div>
              <div className="buttons">
                <button
                  className="secondary-btn"
                  onClick={() => navigate(createAgainPath)}
                >
                  Create Another
                </button>
                <button className="primary-btn" onClick={handleDownload}>
                  <i className="fi fi-rr-download"></i> Download Video
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="status-container"
          >
            <div className="neural-loader">
              <div className="inner-circle"></div>
              <div className="outer-ring"></div>
            </div>

            <h2>
              {status === "failed"
                ? "Generation Failed"
                : "Zullu AI is rendering..."}
            </h2>
            <p>
              {status === "failed"
                ? "There was an issue processing your request. Credits have not been deducted."
                : "You can generate multiple videos at the same time. Your video will be ready in 90 - 120 seconds. Check it in My Creations."}
            </p>

            <div className="status-actions">
              <button
                className="retry-btn"
                onClick={() => navigate(createAgainPath)}
              >
                {status === "failed" ? "Try Again" : "Create another video"}
              </button>
              {status !== "failed" && (
                <button
                  className="my-creations-btn"
                  onClick={() => navigate("/my-creations/videos")}
                >
                  Go to My Creations
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoResult;
